import { useCallback, useEffect, useRef } from 'react';
import { isMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { A11yState } from '../../types';
import {
  DEMO_CONTENT_FOCUS_SELECTOR,
  FORCED_FOCUS_VISIBLE_ATTRIBUTE,
  WIDGET_CLASS
} from '../constants';
import { useDemoToggle } from './use-demo-toggle';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
];

export function useFocusFirstVisibleElement({ keyboardFocus }: A11yState) {
  const { editMode } = useSettings();
  const { toggleState } = useDemoToggle();
  // Needed only for edit-mode "after" transition detection.
  const previousToggleStateRef = useRef(toggleState);

  const isMobileDevice = isMobile();

  const applyFocusFirstVisibleElement = keyboardFocus;

  const isVisibleAndFocusable = useCallback((element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);

    const isVisible =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.top >= 0 &&
      rect.top < window.innerHeight &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      !element.hasAttribute('disabled');

    return isVisible;
  }, []);

  const focusFirstElement = useCallback(() => {
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS.join(','))
    ).filter((element) => {
      const isInsideWidget = element.classList.value.includes(WIDGET_CLASS);
      const isAriaHidden = element.closest('[aria-hidden="true"]') !== null;

      return isVisibleAndFocusable(element) && !isInsideWidget && !isAriaHidden;
    });

    if (elements.length > 0) {
      elements[0].focus({ preventScroll: true });
    }
  }, [isVisibleAndFocusable]);

  const focusDemoElement = useCallback(() => {
    const demoContainer = document.querySelector<HTMLElement>(
      DEMO_CONTENT_FOCUS_SELECTOR
    );

    if (!demoContainer) {
      return false;
    }

    const demoElements = Array.from(
      demoContainer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS.join(','))
    );

    const firstFocusableDemoElement = demoElements.find((element) => {
      const isAriaHidden = element.closest('[aria-hidden="true"]') !== null;

      return isVisibleAndFocusable(element) && !isAriaHidden;
    });

    if (!firstFocusableDemoElement) {
      return false;
    }

    applyProgrammaticFocus(firstFocusableDemoElement);

    return true;
  }, [isVisibleAndFocusable]);

  useEffect(() => {
    if (!applyFocusFirstVisibleElement || isMobileDevice) {
      if (editMode) {
        previousToggleStateRef.current = toggleState;
      }

      return;
    }

    if (editMode) {
      const switchedToAfter =
        toggleState === 'after' && previousToggleStateRef.current !== 'after';

      if (switchedToAfter) {
        focusDemoElement();
      }

      previousToggleStateRef.current = toggleState;
      return;
    }

    focusFirstElement();
  }, [
    applyFocusFirstVisibleElement,
    isMobileDevice,
    editMode,
    toggleState,
    focusFirstElement,
    focusDemoElement
  ]);
}

function applyProgrammaticFocus(target: HTMLElement) {
  document
    .querySelectorAll<HTMLElement>(`[${FORCED_FOCUS_VISIBLE_ATTRIBUTE}="true"]`)
    .forEach((element) =>
      element.removeAttribute(FORCED_FOCUS_VISIBLE_ATTRIBUTE)
    );

  target.setAttribute(FORCED_FOCUS_VISIBLE_ATTRIBUTE, 'true');
  target.addEventListener(
    'blur',
    () => {
      target.removeAttribute(FORCED_FOCUS_VISIBLE_ATTRIBUTE);
    },
    { once: true }
  );

  target.focus({ preventScroll: true });
}
